import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

type ClearCallback = () => void;

interface ThumbnailSelectionContextType {
  activeSectionId: string | null;
  activateSection: (sectionId: string) => void;
  deactivateSection: (sectionId: string) => void;
  registerClear: (sectionId: string, clear: ClearCallback) => () => void;
}

function deferStateUpdate(callback: () => void): void {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(callback);
    return;
  }
  Promise.resolve().then(callback);
}

const ThumbnailSelectionContext = createContext<
  ThumbnailSelectionContextType | undefined
>(undefined);

export function ThumbnailSelectionProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const clearCallbacks = useRef<Map<string, ClearCallback>>(new Map());
  const activeSectionRef = useRef<string | null>(null);
  activeSectionRef.current = activeSectionId;

  const registerClear = useCallback(
    (sectionId: string, clear: ClearCallback) => {
      clearCallbacks.current.set(sectionId, clear);
      return () => {
        clearCallbacks.current.delete(sectionId);
      };
    },
    []
  );

  const activateSection = useCallback((sectionId: string) => {
    const prev = activeSectionRef.current;
    deferStateUpdate(() => {
      if (prev && prev !== sectionId) {
        clearCallbacks.current.get(prev)?.();
      }
      setActiveSectionId(sectionId);
    });
  }, []);

  const deactivateSection = useCallback((sectionId: string) => {
    deferStateUpdate(() => {
      setActiveSectionId((prev) => (prev === sectionId ? null : prev));
    });
  }, []);

  return (
    <ThumbnailSelectionContext.Provider
      value={{ activeSectionId, activateSection, deactivateSection, registerClear }}
    >
      {children}
    </ThumbnailSelectionContext.Provider>
  );
}

export function useThumbnailSelectionContext(): ThumbnailSelectionContextType | undefined {
  return useContext(ThumbnailSelectionContext);
}
